import { clsx } from '@alexis/helpers/clsx';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchSort from '@pages/FlightSearchResult/hooks/useFlightSearchSort';
import React from 'react';
import { useSelector } from 'react-redux';

import Price from '@components/Price';

import {
  getCurrencyState,
  getIsRtlState,
  getLocaleState,
  getTranslationsState,
} from '@redux/selectors';

import type { FlightSortType } from '@wegoTypes/flights/flightSortType';
import type { SortOrder } from '@wegoTypes/sortOrder';

import styles from './FlightSortButtonTab.module.scss';

export type FlightSortButtonTabProps = {
  cheapestFare: Price | undefined;
  sortOrder: SortOrder;
  sortType: FlightSortType;
  translationKey: string;
  handleOnSortTypeChanged(sortType: FlightSortType, sortOrder: SortOrder): void;
};

const FlightSortButtonTab: React.FC<FlightSortButtonTabProps> = ({
  cheapestFare,
  sortOrder,
  sortType,
  translationKey,
  handleOnSortTypeChanged,
}) => {
  const currency = useSelector(getCurrencyState);
  const isRtl = useSelector(getIsRtlState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const { exchangeRate, isLoading } = useFlightSearchResults();
  const { sortOrder: selectedSortOrder, sortType: selectedSortType } = useFlightSearchSort();

  return (
    <button
      className={clsx(
        styles.sortButton,
        selectedSortType === sortType && selectedSortOrder === sortOrder && styles.active,
        isRtl && styles.rtl,
      )}
      onClick={() => handleOnSortTypeChanged(sortType, sortOrder)}
    >
      <div className={clsx(styles.header, styles.withPrice)}>
        <div className={clsx(styles.label, styles.withEllipsis)}>
          {translations[translationKey]}
        </div>

        <div className={styles.priceTimeContainer}>
          {cheapestFare ? (
            <Price
              isInline
              className={styles.price}
              price={cheapestFare}
              locale={locale}
              currency={currency}
              exchangeRate={exchangeRate}
            />
          ) : isLoading ? (
            <div className={styles.skeleton}></div>
          ) : null}
        </div>
      </div>
    </button>
  );
};

export default FlightSortButtonTab;
